import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createPost, fetchPosts, deletePost } from '../../../store/campaignActions';
import { setSelectedPost } from '../../../store/campaignSlice';
import { 
  selectPosts, 
  selectSelectedGroups, 
  selectIsLoading, 
  selectSelectedPost 
} from '../../../store/selectors';

const validateTemplateName = (name) => {
  const templateNameRegex = /^[a-z_]+$/;
  return templateNameRegex.test(name);
};

const formatTemplateName = (name) => {
  return name.toLowerCase().replace(/[^a-z_]/g, '_').replace(/_+/g, '_');
};

export const useStepsPostLogic = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const posts = useSelector(selectPosts);
  const selectedGroups = useSelector(selectSelectedGroups);
  const isLoading = useSelector(selectIsLoading);
  const selectedPost = useSelector(selectSelectedPost);

  const [formData, setFormData] = useState({
    title: '',
    postText: '',
    postImage: null,
    buttons: []
  });

  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchPosts());
  }, [dispatch]);

  const handleFormChange = (newFormData) => {
    setFormData(newFormData);
  };

  const handlePostSelect = (postId) => {
    const post = posts.find(p => p.id === postId);
    if (post) {
      dispatch(setSelectedPost(post));
    }
  };

  const handleEditPost = (post) => {
    setFormData({
      title: post.template_name,
      postText: post.body,
      postImage: post.header,
      buttons: (post.buttons || []).map(button => ({
        type: button.action_type,
        text: button.label,
        action: button.button_action
      }))
    });
    dispatch(setSelectedPost(post));
  };

  const handleCreatePost = async (postData) => {
    try {
      if (!postData.title || !postData.title.trim()) {
        setAlert({ open: true, message: 'Template name is required', severity: 'error' });
        return;
      }

      const formattedTitle = formatTemplateName(postData.title.trim());
      
      if (!validateTemplateName(formattedTitle)) {
        setAlert({ 
          open: true, 
          message: 'Template name must contain only lowercase letters and underscores. Auto-formatting applied.', 
          severity: 'warning' 
        });
      }

      const validatedPostData = {
        ...postData,
        title: formattedTitle
      };

      await dispatch(createPost(validatedPostData)).unwrap();
      setAlert({ open: true, message: 'Post created successfully!', severity: 'success' });
      setFormData({
        title: '',
        postText: '',
        postImage: null,
        buttons: []
      });
      dispatch(fetchPosts());
    } catch (err) {
      const errorMessage = err.message || 'Failed to create post';
      if (errorMessage.includes('template_name')) {
        setAlert({ 
          open: true, 
          message: 'Invalid template name format. Use only lowercase letters and underscores.', 
          severity: 'error' 
        });
      } else {
        setAlert({ open: true, message: errorMessage, severity: 'error' });
      }
    }
  };

  const handleDeleteClick = (postId) => {
    setPostToDelete(postId);
    setIsConfirmationOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deletePost(postToDelete)).unwrap();
      setAlert({ open: true, message: 'Post deleted successfully!', severity: 'success' });
      if (selectedPost && selectedPost.id === postToDelete) {
        dispatch(setSelectedPost(null));
      }
      dispatch(fetchPosts());
      setIsConfirmationOpen(false);
      setPostToDelete(null);
    } catch (err) {
      setAlert({ open: true, message: 'Failed to delete post', severity: 'error' });
    }
  };

  const handleNext = () => {
    if (selectedPost) {
      navigate('/campaign/steps/deploy');
    }
  };

  return {
    posts,
    selectedPost,
    selectedGroups,
    formData,
    isLoading,
    alert,
    isConfirmationOpen,
    postToDelete,
    handleFormChange,
    handlePostSelect,
    handleEditPost,
    handleCreatePost,
    handleDeleteClick,
    handleDeleteConfirm,
    handleNext,
    setIsConfirmationOpen,
    validateTemplateName,
    formatTemplateName
  };
};